"use client";

import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  applyBrandPalette,
  DEFAULT_BRAND_COLOR,
  normalizeHex,
} from "@/lib/brand-color";

const STORAGE_KEY = "gnb-brand-color";

type BrandColorContextType = {
  brandColor: string;
  setBrandColor: (hex: string) => void;
  resetBrandColor: () => void;
};

const BrandColorContext = createContext<BrandColorContextType | undefined>(
  undefined,
);

export const BrandColorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [brandColor, setBrandColorState] = useState(DEFAULT_BRAND_COLOR);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const initial = normalizeHex(saved ?? "") ?? DEFAULT_BRAND_COLOR;
    setBrandColorState(initial);
    applyBrandPalette(initial);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    applyBrandPalette(brandColor);
    localStorage.setItem(STORAGE_KEY, brandColor);
  }, [brandColor, ready]);

  const setBrandColor = useCallback((hex: string) => {
    const next = normalizeHex(hex);
    if (next) setBrandColorState(next);
  }, []);

  const resetBrandColor = useCallback(() => {
    setBrandColorState(DEFAULT_BRAND_COLOR);
  }, []);

  return (
    <BrandColorContext.Provider
      value={{ brandColor, setBrandColor, resetBrandColor }}
    >
      {children}
    </BrandColorContext.Provider>
  );
};

export function useBrandColor() {
  const ctx = useContext(BrandColorContext);
  if (!ctx) {
    throw new Error("useBrandColor must be used within BrandColorProvider");
  }
  return ctx;
}
